import type { LucideIcon } from 'lucide-react';
import { useCallback, useId, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../../lib/cx.ts';
import { useOnClickOutside } from '../../lib/hooks.ts';
import { computePosition } from './position.ts';
import './Menu.css';

export type MenuEntry =
  | { type?: 'item'; key: string; label: ReactNode; icon?: LucideIcon; onSelect: () => void; danger?: boolean; disabled?: boolean; hint?: ReactNode }
  | { type: 'separator'; key: string }
  | { type: 'label'; key: string; label: ReactNode };

export interface MenuTriggerProps {
  ref: (el: HTMLElement | null) => void;
  onClick: () => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => void;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls': string | undefined;
}

export interface DropdownMenuProps {
  /** Render the trigger and spread the given props onto the focusable element. */
  trigger: (props: MenuTriggerProps) => ReactNode;
  items: readonly MenuEntry[];
  /** Accessible name of the menu. */
  label: string;
  align?: 'start' | 'end';
  header?: ReactNode;
  minWidth?: number;
  className?: string;
}

/** Menu button: arrows/Home/End/type-ahead, Enter to select, Esc restores focus to the trigger. */
export function DropdownMenu({ trigger, items, label, align = 'end', header, minWidth = 220, className }: DropdownMenuProps) {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const initialFocus = useRef<'first' | 'last'>('first');

  const enabledItems = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? []);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    setPos(null);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const openMenu = (focus: 'first' | 'last') => {
    initialFocus.current = focus;
    setOpen(true);
  };

  useOnClickOutside([menuRef, triggerRef], () => close(false), open);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const t = triggerRef.current;
      const m = menuRef.current;
      if (!t || !m) return;
      const r = computePosition(t.getBoundingClientRect(), { width: m.offsetWidth, height: m.offsetHeight }, 'bottom', align, 6);
      setPos({ top: r.top, left: r.left });
    };
    place();
    const list = enabledItems();
    (initialFocus.current === 'last' ? list[list.length - 1] : list[0])?.focus({ preventScroll: true });
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, align]);

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const list = enabledItems();
    if (list.length === 0) return;
    const idx = list.indexOf(document.activeElement as HTMLButtonElement);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        list[(idx + 1) % list.length]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        list[idx <= 0 ? list.length - 1 : idx - 1]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        list[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        list[list.length - 1]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        close(true);
        break;
      case 'Tab':
        close(false);
        break;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          const ch = e.key.toLocaleLowerCase('th');
          const ordered = [...list.slice(idx + 1), ...list.slice(0, idx + 1)];
          ordered.find((el) => (el.textContent ?? '').trim().toLocaleLowerCase('th').startsWith(ch))?.focus();
        }
    }
  };

  const triggerProps: MenuTriggerProps = {
    ref: (el) => {
      triggerRef.current = el;
    },
    onClick: () => (open ? close(false) : openMenu('first')),
    onKeyDown: (e) => {
      if (open) return;
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openMenu('first');
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        openMenu('last');
      }
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
  };

  return (
    <>
      {trigger(triggerProps)}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={label}
            className={cx('menu', className)}
            style={{ minWidth, top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
            onKeyDown={onMenuKeyDown}
          >
            {header && (
              <div className="menu__header" role="presentation">
                {header}
              </div>
            )}
            {items.map((item) => {
              if (item.type === 'separator') return <div key={item.key} role="separator" className="menu-separator" />;
              if (item.type === 'label')
                return (
                  <div key={item.key} role="presentation" className="menu-label">
                    {item.label}
                  </div>
                );
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  aria-disabled={item.disabled || undefined}
                  className={cx('menu-item', item.danger && 'menu-item--danger')}
                  onClick={() => {
                    if (item.disabled) return;
                    close(true);
                    item.onSelect();
                  }}
                >
                  {Icon && <Icon size={18} strokeWidth={1.75} aria-hidden="true" />}
                  <span className="menu-item__label">{item.label}</span>
                  {item.hint && <span className="menu-item__hint">{item.hint}</span>}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
